import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { API_URL } from '../../helpers/globals';
import { AuthService } from '../auth/auth.service';

export interface CorruptedFile {
    id: number;
    fileOriginalName: string;
    fileSavePath: string;
    fileUploadTime: number;
    userId: number;
    userName: string;
    clientId: number;
    identificationName: string;
    identificationNumber: string;
    uploadSource: string;
    errorMsg: string;
    seen: boolean;
}

export interface CorruptedFilesResponse {
    Records: CorruptedFile[];
    TotalRecordCount: number;
}

export interface HasFilesWithErrorResponse {
    HasFilesWithError: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CorruptedFilesService {

    private _hasFilesWithError = new BehaviorSubject<boolean>(false);
    public hasFilesWithError$ = this._hasFilesWithError.asObservable();

    constructor(private http: HttpClient, private authService: AuthService) {
        // Subscribe to company changes and check for corrupted files
        this.authService.currentCompanyId.subscribe((newCompanyId) => {
            if (newCompanyId !== 0) {
                this.checkHasFilesWithError(newCompanyId).subscribe();
            }
        });
    }

    checkHasFilesWithError(clientId: number): Observable<HasFilesWithErrorResponse> {
        return this.http.get<HasFilesWithErrorResponse>(
            `${API_URL}hasFilesWithError`,
            { params: { clientId: clientId.toString() }, withCredentials: true }
        ).pipe(
            tap(response => {
                this._hasFilesWithError.next(response.HasFilesWithError);
            })
        );
    }

    getCorruptedFiles(clientId: number): Observable<CorruptedFilesResponse> {
        return this.http.get<CorruptedFilesResponse>(
            `${API_URL}getFilesWithError`,
            { params: { clientId: clientId.toString() }, withCredentials: true }
        );
    }

    deleteCorruptedFiles(fileIds: number[], clientId: number): Observable<any> {
        return this.http.post(
            `${API_URL}deleteFilesWithError`,
            { fileIds, clientId },
            { withCredentials: true }
        );
    }

    downloadCorruptedFiles(fileIds: number[], clientId: number): Observable<HttpResponse<Blob>> {
        return this.http.post(
            `${API_URL}downloadFilesWithError`,
            { fileIds, clientId },
            {
                responseType: 'blob',
                observe: 'response',
                withCredentials: true
            }
        );
    }

    // TODO: Replace with actual API endpoint when provided
    markAllAsSeen(clientId: number): Observable<any> {
        // Placeholder - API endpoint will be provided later
        return this.http.post(
            `${API_URL}seenCorruptedFiles?clientId=${clientId}`,
            {},
            { withCredentials: true }
        ).pipe(
            tap(() => {
                // Refresh hasFilesWithError status
                this.checkHasFilesWithError(clientId).subscribe();
            })
        );
    }
}
